import { Clipboard } from '@angular/cdk/clipboard';
import { Component, inject, OnInit } from '@angular/core';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialAdminControllerService,
  ItauCobrancaResponse
} from 'src/app/admin-api';
import { TableActionButton, TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';

@Component({
  selector: 'app-cobrancas',
  templateUrl: './cobrancas.component.html',
  styleUrl: './cobrancas.component.scss'
})
export class CobrancasComponent implements OnInit {
  financialAdminService = inject(FinancialAdminControllerService);
  private messageService = inject(MessageService);
  private clipboard = inject(Clipboard);
  private confirmationService = inject(ConfirmationService);

  colsCob = [
    new TableColumn.Builder().setHeader('TxId').setField('txid').build(),
    new TableColumn.Builder()
      .setHeader('Criação')
      .setField('calendario.criacao')
      .setType('date')
      .setCondition('between')
      .setRequired(true)
      .build(),
    new TableColumn.Builder()
      .setHeader('Expiração')
      .setField('calendario.horarioExpiracao')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Descrição')
      .setField('infoAdicionais.4.valor')
      .setCondition('contains')
      .build(),
    new TableColumn.Builder()
      .setHeader('Nome')
      .setField('devedor.nome')
      .setRouterLink('/users/person/')
      .setRouterLinkFieldName('infoAdicionais.0.valor')
      .build(),
    new TableColumn.Builder()
      .setHeader('CPF')
      .setField('devedor.cpf')
      .setType('number')
      .setDisplayFunction((charge: ItauCobrancaResponse) =>
        charge.devedor?.cpf
          ? FormUtil.cpfFormatted(FormUtil.onlyNumbers(charge.devedor?.cpf))
          : null
      )
      .build(),
    new TableColumn.Builder()
      .setHeader('Email')
      .setField('infoAdicionais.1.valor')
      .build(),
    new TableColumn.Builder()
      .setHeader('SubscriberId')
      .setField('infoAdicionais.2.valor')
      .setRouterLink('/users/subscribers/')
      .setRouterLinkFieldName('infoAdicionais.2.valor')
      .build(),
    new TableColumn.Builder()
      .setHeader('CreditCardPaymentId')
      .setField('infoAdicionais.3.valor')
      .setType('number')
      .build(),
    new TableColumn.Builder().setHeader('Status').setField('status').build(),
    new TableColumn.Builder()
      .setHeader('Valor')
      .setField('valor.original')
      .setType('currency')
      .build(),
    new TableColumn.Builder()
      .setHeader('Ação')
      .setField('')
      .setType('button')
      .build()
  ];

  dropdownFiltersCob = {
    status: Object.keys(ItauCobrancaResponse.StatusEnum)
      .map((key) => ({
        label: key,
        value: ItauCobrancaResponse.StatusEnum[key]
      }))
      .concat([{ label: 'Todos', value: null }])
  };

  defaultFilters = {
    'calendario.criacao': [...this.startRange],
    status: null
  };
  cobrancas: ItauCobrancaResponse[];
  askCancelCharge = (cobranca: ItauCobrancaResponse, $event: MouseEvent) => {
    this.confirmationService.confirm({
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Cancelar',
      rejectLabel: 'Manter',
      rejectButtonStyleClass: 'p-button-primary',
      message: `Tem certeza que deseja cancelar a cobrança "${cobranca.txid}"?`,
      header: 'Confirmação',
      icon: PrimeIcons.EXCLAMATION_TRIANGLE,
      target: $event.target,
      accept: async () => await this.cancelCharge(cobranca)
    });
  };
  buttons = [
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.COPY)
      .setTooltip('Copiar código pix')
      .setTooltipPosition('bottom')
      .setAction(async (cobranca: ItauCobrancaResponse) => {
        if (
          (cobranca.calendario.horarioExpiracao as any).getTime() >
          new Date().getTime()
        )
          this.copyToClipboard(cobranca.pixCopiaECola);
        else
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'O código pix desta cobrança já expirou.'
          });
      })
      .setDisplay(
        (c: ItauCobrancaResponse) =>
          c.pixCopiaECola &&
          c.status === ItauCobrancaResponse.StatusEnum.ATIVA &&
          (c.calendario.horarioExpiracao as any).getTime() >
            new Date().getTime()
      )
      .setText(true)
      .build(),
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.TIMES_CIRCLE)
      .setTooltip('Cancelar cobrança')
      .setTooltipPosition('bottom')
      .setSeverity('danger')
      .setAction(this.askCancelCharge)
      .setDisplay(
        (c: ItauCobrancaResponse) =>
          c.status === ItauCobrancaResponse.StatusEnum.ATIVA
      )
      .setText(true)
      .build()
  ];

  async ngOnInit() {
    await this.findCobrancas();
  }

  async findCobrancas(range?: Date[]) {
    try {
      LoaderService.showLoader();
      this.cobrancas = await lastValueFrom(
        this.financialAdminService
          .findCobrancas(
            (range || this.startRange)?.at(0)?.toISOString(),
            (range || this.startRange)?.at(1)?.toISOString()
          )
          .pipe(
            map((data) =>
              data.result.map((r: ItauCobrancaResponse) => {
                if (r.calendario?.criacao)
                  r.calendario.criacao = new Date(r.calendario.criacao) as any;
                if (r.calendario?.horarioExpiracao)
                  r.calendario.horarioExpiracao = new Date(
                    r.calendario.horarioExpiracao
                  ) as any;
                r.infoAdicionais = [
                  r.infoAdicionais?.find((i) => i.nome === 'personId'),
                  r.infoAdicionais?.find((i) => i.nome === 'email'),
                  r.infoAdicionais?.find((i) => i.nome === 'subscriberId'),
                  r.infoAdicionais?.find(
                    (i) => i.nome === 'creditCardPaymentId'
                  ),
                  r.infoAdicionais?.find((i) => i.nome === 'description')
                ];
                return r;
              })
            )
          )
      );
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async cancelCharge(cobranca: ItauCobrancaResponse) {
    if (cobranca.status === ItauCobrancaResponse.StatusEnum.ATIVA) {
      try {
        LoaderService.showLoader();
        cobranca = await lastValueFrom(
          this.financialAdminService
            .cancelInstantCharge(cobranca.txid)
            .pipe(map((data) => data.result))
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Cobrança cancelada com sucesso'
        });
        this.cobrancas
          ?.filter((c) => c.txid === cobranca.txid)
          .forEach((c) => (c.status = cobranca.status));
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    }
  }

  copyToClipboard = (value: string) => {
    this.clipboard.copy(value);
    this.messageService.add({
      severity: 'info',
      summary: 'Copiado',
      detail: 'Código copiado para área de transferência'
    });
  };

  get startRange() {
    const date = new Date(new Date().setDate(new Date().getDate() - 1));
    date.setHours(0, 0, 0, 0);
    return [date, new Date()];
  }
}
